// Read-side heartbeat helpers — browser-safe.
//
// Why this is separate from ./index.ts:
//   The writer (`startHeartbeat`) uses `node:os` (hostname()) at module
//   scope and therefore can't be imported into a Vite browser bundle.
//   The web dashboard's /agents page needs the read helpers
//   (`classifyHeartbeat`, `readAllHeartbeats`, the schema + types) but
//   never calls the writer. Splitting the file keeps the dashboard build
//   clean without polyfills or bundler tricks.
//
// Source of truth: this file owns the schema + read helpers. ./index.ts
// re-exports them so existing daemon code keeps working unchanged.

import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';

export const HEARTBEAT_KEY_PREFIX = 'daemon_heartbeat:';

export const DaemonNameSchema = z
  .string()
  .min(1)
  .regex(/^[a-z][a-z0-9_]*$/, 'daemon name must be lowercase snake_case');

// Optional per-daemon status bag. Liveness stays in the fixed fields above;
// `info` lets a daemon publish a few scalar facts about WHAT it's doing (e.g.
// the action-item-email daemon's drafting engine) that a UI can read via the
// owner-readable daemon_heartbeat:* key. Scalars only — no PII, no secrets.
export const HeartbeatInfoSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type HeartbeatInfo = z.infer<typeof HeartbeatInfoSchema>;

export const HeartbeatPayloadSchema = z.object({
  last_seen_at: z.string().datetime(),
  started_at: z.string().datetime(),
  host: z.string(),
  tick_count: z.number().int().nonnegative(),
  interval_seconds: z.number().int().positive(),
  last_error: z.string().nullable(),
  last_error_at: z.string().datetime().nullable(),
  // Optional + nullish so heartbeats written before this field existed still parse.
  info: HeartbeatInfoSchema.nullish(),
});

export type HeartbeatPayload = z.infer<typeof HeartbeatPayloadSchema>;

const WATCHDOG_WARN_MULTIPLIER = 1.5;
const WATCHDOG_ALERT_MULTIPLIER = 3;

// Daemons that run on the OWNER'S LAPTOP rather than on a server. They stop
// whenever the lid closes, which is documented, expected behaviour — CLAUDE.md
// §5: capture pauses while the Mac is shut and catches up on resume. Everything
// that judges their liveness needs to know that, so the list lives here (the
// browser-safe module) rather than in the workers process alone.
const CAPTURE_DAEMONS: Record<string, string> = {
  bridge_imessage: 'iMessage',
  bridge_whatsapp: 'WhatsApp (linked device)',
  bridge_whatsapp_local: 'WhatsApp',
  bridge_sms: 'SMS',
};

/** Human channel name for a daemon, falling back to the raw name. */
export function captureChannelLabel(daemon: string): string {
  return CAPTURE_DAEMONS[daemon] ?? daemon;
}

export function isCaptureDaemon(daemon: string): boolean {
  return daemon in CAPTURE_DAEMONS;
}

// How stale a capture daemon must be before "asleep" stops being the likeliest
// explanation. Measured on the owner's Mac: 6-8 hour gaps every night tracking
// Pacific night hours, on a process that ran 4.5 days without restarting. 12h
// clears a night's sleep; the real outages this exists to catch ran 2d18h and
// ~7 days, so both are still caught within half a day.
export const DEFAULT_CAPTURE_SILENCE_FLOOR_SECONDS = 12 * 60 * 60;

export type HeartbeatStatus = 'alive' | 'warning' | 'alert' | 'asleep';

export type HeartbeatClassification = {
  status: HeartbeatStatus;
  staleness_seconds: number;
};

export type ClassifyOptions = {
  /** Needed to tell a laptop daemon from a server one. */
  daemonName?: string;
  /** Below this, a stale capture daemon classifies as 'asleep' rather than
   *  'alert'. Named 'asleep', not 'paused': /agents already uses 'paused' for a
   *  daemon whose kill-switch is off, which is a different fact entirely. Omit `daemonName` (or pass a server daemon) and this has no
   *  effect, so existing callers keep the old three-state behaviour exactly. */
  captureFloorSeconds?: number;
};

/**
 * Classify a heartbeat's freshness.
 *
 * Without `opts` this is the original three-state result, so every existing
 * caller is unaffected. Pass `opts.daemonName` to get 'asleep' for a capture
 * daemon on a sleeping laptop — the distinction that stops the dashboard
 * reporting a closed lid as a dead bridge.
 */
export function classifyHeartbeat(
  payload: HeartbeatPayload,
  now: Date,
  opts: ClassifyOptions = {},
): HeartbeatClassification {
  const lastSeenMs = Date.parse(payload.last_seen_at);
  const stalenessMs = Math.max(0, now.getTime() - lastSeenMs);
  const stalenessSeconds = Math.round(stalenessMs / 1000);
  const intervalSeconds = payload.interval_seconds;

  let status: HeartbeatStatus = 'alive';
  if (stalenessSeconds >= intervalSeconds * WATCHDOG_ALERT_MULTIPLIER) {
    status = 'alert';
  } else if (stalenessSeconds >= intervalSeconds * WATCHDOG_WARN_MULTIPLIER) {
    status = 'warning';
  }

  // Only ever downgrades 'alert' — a fresh or mildly stale heartbeat is never
  // reported as asleep.
  if (
    status === 'alert' &&
    opts.daemonName !== undefined &&
    isCaptureDaemon(opts.daemonName) &&
    stalenessSeconds < (opts.captureFloorSeconds ?? DEFAULT_CAPTURE_SILENCE_FLOOR_SECONDS)
  ) {
    status = 'asleep';
  }

  return { status, staleness_seconds: stalenessSeconds };
}

export type HeartbeatRow = {
  daemon_name: string;
  payload: HeartbeatPayload | null;
  parse_error?: string;
};

/** Read one daemon's heartbeat row (or null if it has never beat). */
export async function readHeartbeat(
  supabase: SupabaseClient,
  daemonName: string,
): Promise<HeartbeatRow | null> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', `${HEARTBEAT_KEY_PREFIX}${daemonName}`)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const value = (data as { value: string | null }).value;
  if (!value) return { daemon_name: daemonName, payload: null, parse_error: 'value is null' };
  try {
    return { daemon_name: daemonName, payload: HeartbeatPayloadSchema.parse(JSON.parse(value)) };
  } catch (err) {
    return {
      daemon_name: daemonName,
      payload: null,
      parse_error: err instanceof Error ? err.message : String(err),
    };
  }
}

export async function readAllHeartbeats(supabase: SupabaseClient): Promise<HeartbeatRow[]> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('key, value')
    .like('key', `${HEARTBEAT_KEY_PREFIX}%`);
  if (error) throw error;
  if (!data) return [];

  return data.map((row: { key: string; value: string | null }): HeartbeatRow => {
    const daemon_name = row.key.slice(HEARTBEAT_KEY_PREFIX.length);
    if (!row.value) {
      return { daemon_name, payload: null, parse_error: 'value is null' };
    }
    try {
      const parsed = HeartbeatPayloadSchema.parse(JSON.parse(row.value));
      return { daemon_name, payload: parsed };
    } catch (err) {
      return {
        daemon_name,
        payload: null,
        parse_error: err instanceof Error ? err.message : String(err),
      };
    }
  });
}
